// GenericCaptureRenderer.jsx (v2)
// Renders a capture tab from a JSON config matching capture-tab-schema.json.
// v2 fixes the storage model to match the rest of the app: every block's
// per-item state is stored FLAT at the root of the useSummitSync data object,
// keyed by the item's own id (dimension id, row id, area id, etc) -- exactly
// how S3/S4/etc already work by hand. Nesting under a block-id wrapper (v1)
// would have silently broken computeLiveValue()'s direct s3.strategy.score
// style reads if this renderer were ever swapped in live.
//
// gate_checklist items are the one place a prefix is applied ("gate_" + id),
// mirroring S3's own hand-written convention for avoiding collisions with
// dimension ids in the same flat namespace. narrative entries and matrix
// extra-added-rows have no natural per-item id, so they live under a
// block-scoped key ("_entries_"+block.id / "_extraRows_"+block.id).
//
// Build order (see editor-and-renderer-plan.md):
//   1. Schema (done, v2 extends scorecard + adds gate_checklist + footer)
//   2. This renderer                                            <- this file
//   3. Migrate one real session (S3) to a JSON config, verify match
//   4. Monaco + ajv + live-preview editor in AdminTab.jsx
//   5. Migrate remaining sessions, retire old hardcoded JSX
//   6. ALL_TABS sourced from tabConfigs

import { useState } from "react";
import useSummitSync from "./useSummitSync";
import { computeStatTile } from "./statTile";
import { evaluateFieldPredicate } from "./fieldPredicate";
import { DECISION_STATUSES, TCC_OWNERS, DUE_DATES } from "./captureUtils";
import { useOrgDirectory } from "./OrgDirectoryContext";
import { resolveOwnerNames } from "./ownerResolution";

const C = {
  dark: "#1a1a2e", gold: "#c9a84c", white: "#ffffff",
  border: "#e5e7eb", muted: "#6b7280", text: "#374151",
  green: "#27ae60", orange: "#e67e22", red: "#c0392b",
  blue: "#2980b9", yellow: "#f59e0b", yellowBg: "#fef3c7",
  light: "#f8f7f4", purple: "#8e44ad",
};

const DEFAULT_SCALE_COLORS = { 1: C.red, 2: C.orange, 3: C.yellow, 4: C.green, 5: "#1abc9c" };

function Card({ children, style }) {
  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14, marginBottom: 12, ...style }}>
      {children}
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.dark, textTransform: "uppercase", letterSpacing: 1 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || C.dark }}>{value}</div>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function FacilitatorNote({ text }) {
  if (!text) return null;
  return (
    <div style={{ background: C.gold + "18", border: "1px solid " + C.gold + "44", borderRadius: 6, padding: "8px 12px", marginBottom: 10, fontSize: 11, color: C.text, fontStyle: "italic" }}>
      <span style={{ fontWeight: 700, fontStyle: "normal", color: C.gold }}>Facilitator: </span>{text}
    </div>
  );
}

function WatchFor({ text }) {
  if (!text) return null;
  return (
    <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "7px 10px", marginBottom: 10, fontSize: 11, color: "#7f1d1d" }}>
      <span style={{ fontWeight: 700 }}>Watch for: </span>{text}
    </div>
  );
}

function ExpectedScoreBadge({ score, label, colorFn }) {
  if (score == null) return null;
  const c = colorFn(score) || C.muted;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: c + "18", border: "1px solid " + c + "44", borderRadius: 6, padding: "4px 10px", marginBottom: 8, fontSize: 11 }}>
      <span style={{ fontWeight: 700, color: c }}>Expected: {score}{label ? " \u2013 " + label : ""}</span>
    </div>
  );
}

function SaveStatusIndicator({ status }) {
  const s = {
    saving: ["Saving...", C.orange],
    saved: ["Saved", C.green],
    error: ["Save failed", C.red],
    unsaved: ["Unsaved", C.muted],
    offline: ["Offline - changes not saved", C.red],
    loading: ["Loading...", C.muted],
  }[status];
  if (!s) return null;
  return <span style={{ fontSize: 11, color: s[1], marginLeft: 8 }}>{s[0]}</span>;
}

function csvDownload(filename, rows) {
  const csv = rows.map(function (r) {
    return r.map(function (c) { return '"' + String(c == null ? "" : c).replace(/"/g, '""') + '"'; }).join(",");
  }).join("\n");
  const a = document.createElement("a");
  a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
  a.download = filename;
  a.click();
}

// Generic input, covers the six inputType values in fieldDef (text is the default).
function FieldInput({ field, value, onChange, placeholderOverride, theme }) {
  const commonStyle = { width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box", fontFamily: "inherit" };
  const ph = placeholderOverride != null ? placeholderOverride : field.placeholder;
  const { orgUsers } = useOrgDirectory();

  if (field.inputType === "textarea") {
    const darkStyle = theme === "dark-gold" ? { background: "#1e2a3a", border: "1px solid #374151", color: C.white } : undefined;
    return <textarea value={value || ""} onChange={function (e) { onChange(e.target.value); }} placeholder={ph || ""} rows={3} style={{ ...commonStyle, resize: "vertical", ...darkStyle }} />;
  }
  if (field.inputType === "select") {
    const opts = field.options || [];
    const isColored = opts.length > 0 && typeof opts[0] === "object";
    if (isColored) {
      const current = opts.find(function (o) { return o.value === value; });
      return (
        <select value={value || ""} onChange={function (e) { onChange(e.target.value); }}
          style={{ ...commonStyle, color: current ? current.color : commonStyle.color, fontWeight: current ? 700 : commonStyle.fontWeight }}>
          <option value="">{ph || "Select..."}</option>
          {opts.map(function (o) { return <option key={o.value} value={o.value} style={{ color: o.color }}>{o.label}</option>; })}
        </select>
      );
    }
    return (
      <select value={value || ""} onChange={function (e) { onChange(e.target.value); }} style={commonStyle}>
        <option value="">{ph || "Select..."}</option>
        {opts.map(function (o) { return <option key={o} value={o}>{o}</option>; })}
      </select>
    );
  }
  if (field.inputType === "owner") {
    const ownerNames = resolveOwnerNames(orgUsers, TCC_OWNERS);
    return (
      <select value={value || ""} onChange={function (e) { onChange(e.target.value); }} style={commonStyle}>
        <option value="">{ph || "Select owner..."}</option>
        {ownerNames.map(function (o) { return <option key={o} value={o}>{o}</option>; })}
      </select>
    );
  }
  if (field.inputType === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    function toggle(o) {
      const next = selected.includes(o) ? selected.filter(function (x) { return x !== o; }) : selected.concat([o]);
      onChange(next);
    }
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {(field.options || []).map(function (o) {
          const active = selected.includes(o);
          return (
            <button key={o} type="button" onClick={function () { toggle(o); }}
              style={{ fontSize: 11, padding: "3px 8px", borderRadius: 12, border: "1px solid " + (active ? C.blue : C.border), background: active ? C.blue : C.white, color: active ? C.white : C.text, cursor: "pointer" }}>
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  if (field.inputType === "number") {
    return <input type="number" value={value == null ? "" : value} onChange={function (e) { onChange(e.target.value === "" ? null : Number(e.target.value)); }} placeholder={ph || ""} style={commonStyle} />;
  }
  if (field.inputType === "checkbox") {
    if (field.banner) {
      const b = field.banner;
      const activeColor = value ? (b.trueColor || C.green) : (b.falseColor || C.orange);
      const label = value ? (b.trueLabel || field.label) : (b.falseLabel || field.label);
      const subtitle = value ? b.trueSubtitle : b.falseSubtitle;
      const bg = b.bg || (activeColor + "18");
      const border = b.border || ("1px solid " + activeColor + "44");
      const subtitleColor = b.subtitleColor || activeColor;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: bg, border: border, borderRadius: 8, padding: "12px 16px" }}>
          <input type="checkbox" checked={!!value} onChange={function (e) { onChange(e.target.checked); }} style={{ cursor: "pointer" }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: activeColor }}>{label}</div>
            {subtitle && <div style={{ fontSize: 11, color: subtitleColor }}>{subtitle}</div>}
          </div>
        </div>
      );
    }
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: value ? C.green : C.muted, fontWeight: value ? 700 : 400, cursor: "pointer" }}>
        <input type="checkbox" checked={!!value} onChange={function (e) { onChange(e.target.checked); }} style={{ cursor: "pointer" }} />
        {field.label}
      </label>
    );
  }
  if (field.inputType === "date") {
    return <input type="date" value={value || ""} onChange={function (e) { onChange(e.target.value); }} style={commonStyle} />;
  }
  if (field.inputType === "rating") {
    const n = value || 0;
    return (
      <div style={{ display: "flex", gap: 4 }}>
        {[1, 2, 3, 4, 5].map(function (i) {
          return (
            <button key={i} type="button" onClick={function () { onChange(i); }}
              style={{ width: 24, height: 24, borderRadius: 4, border: "1px solid " + C.border, background: i <= n ? C.gold : C.white, color: i <= n ? C.white : C.muted, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
              {i}
            </button>
          );
        })}
      </div>
    );
  }
  return <input type="text" value={value || ""} onChange={function (e) { onChange(e.target.value); }} placeholder={ph || ""} style={commonStyle} />;
}

// Seeds a FLAT default object: every item across every block contributes
// directly to the root, keyed by its own id (matching how S3/S4/etc are
// hand-written today). See file header for the two block-scoped exceptions.
function buildDefaultData(tabConfig) {
  const data = {};
  (tabConfig.blocks || []).forEach(function (block) {
    if (block.type === "scorecard") {
      const captureFields = (block.config.captureFields && block.config.captureFields.length) ? block.config.captureFields : ["note"];
      (block.config.dimensions || []).forEach(function (d) {
        const entry = { score: 0 };
        if (captureFields.includes("note")) entry.note = "";
        if (captureFields.includes("presenter")) entry.presenter = "";
        if (captureFields.includes("owner")) entry.owner = "";
        if (captureFields.includes("gap")) entry.gap = "";
        if (captureFields.includes("action")) entry.action = "";
        if (captureFields.includes("due")) entry.due = "";
        if (captureFields.includes("agreed")) entry.agreed = false;
        data[d.id] = entry;
      });
    } else if (block.type === "gate_checklist") {
      const tristate = block.config.statusMode === "tristate";
      const firstStatus = (block.config.tristateOptions && block.config.tristateOptions[0]) || "not-met";
      (block.config.items || []).forEach(function (item) {
        data["gate_" + item.id] = tristate ? { status: firstStatus } : { met: false, notes: "" };
      });
    } else if (block.type === "matrix") {
      (block.config.rows || []).forEach(function (r) {
        const cells = {};
        (block.config.columns || []).forEach(function (c) { cells[c.id] = c.inputType === "multiselect" ? [] : ""; });
        data[r.id] = cells;
      });
      data["_extraRows_" + block.id] = [];
    } else if (block.type === "list") {
      (block.config.items || []).forEach(function (it) {
        const fields = {};
        (block.config.fields || []).forEach(function (f) {
          if (f.defaultFromItemKey && it[f.defaultFromItemKey] !== undefined) fields[f.id] = it[f.defaultFromItemKey];
          else fields[f.id] = f.inputType === "multiselect" ? [] : "";
        });
        data[it.id] = fields;
      });
    } else if (block.type === "narrative") {
      data[block.config.storageKey || ("_entries_" + block.id)] = [];
    } else if (block.type === "tracker") {
      (block.config.items || []).forEach(function (it) {
        const entry = { status: "", phase: "" };
        (block.config.fields || []).forEach(function (f) {
          if (f.id === "status" || f.id === "phase") return;
          if (f.defaultFromItemKey && it[f.defaultFromItemKey] !== undefined) entry[f.id] = it[f.defaultFromItemKey];
          else if (f.defaultValue !== undefined) entry[f.id] = f.defaultValue;
          else entry[f.id] = f.inputType === "multiselect" ? [] : "";
        });
        data[it.id] = entry;
      });
    } else if (block.type === "ranked_scorecard") {
      (block.config.items || []).forEach(function (it) {
        const entry = {};
        (block.config.dimensions || []).forEach(function (d) {
          entry[d.id] = (it.initialScores && it.initialScores[d.id] != null) ? it.initialScores[d.id] : 0;
        });
        entry.owner = "";
        entry.phase = it.initialPhase || "";
        entry.notes = "";
        entry.deferred_reason = "";
        entry.dataDeps = "";
        data[it.id] = entry;
      });
      if (block.config.confirmationCard) {
        data["_confirmed_" + block.id] = false;
        data["_confirmedNotes_" + block.id] = "";
      }
    } else if (block.type === "settings_card") {
      const doc = {};
      (block.config.fields || []).forEach(function (f) { doc[f.key] = ""; });
      (block.config.footerFields || []).forEach(function (f) { doc[f.key] = f.defaultValue != null ? f.defaultValue : ""; });
      if (block.config.defaultStatus && doc.status === undefined) doc.status = block.config.defaultStatus;
      data[block.config.dataKey] = doc;
    } else if (block.type === "story_tracker") {
      (block.config.items || []).forEach(function (it) {
        data[it.id] = {
          biz: "", bi: "",
          status: (block.config.statusOptions && block.config.statusOptions[0]) || "not-started",
          dataGaps: it.keyGap || "", dataGapOwner: "", dataGapDue: "",
          definitionOfDone: it.dod || "", metrics: "", sourceSystems: "",
          buildEstimate: "", dependencies: "",
          p2Priority: it.p2Priority != null ? it.p2Priority : null
        };
      });
    } else if (block.type === "decision_log" || block.type === "commitment_log" || block.type === "current_state") {
      (block.config.areas || []).forEach(function (a) {
        const fields = {};
        (block.config.captureFields || []).forEach(function (f) { fields[f] = (f === "acknowledged" || f === "agreed") ? false : ""; });
        data[a.id] = fields;
      });
    } else if (block.type === "field_group") {
      (block.config.fields || []).forEach(function (f) {
        if (f.defaultValue !== undefined) data[f.id] = f.defaultValue;
        else data[f.id] = f.inputType === "multiselect" ? [] : "";
      });
    } else if (block.type === "calculator") {
      (block.config.inputs || []).forEach(function (inp) { data[inp.key] = ""; });
    }
  });
  if (tabConfig.footer) {
    if (tabConfig.footer.acknowledgmentKey) data[tabConfig.footer.acknowledgmentKey] = false;
    if (tabConfig.footer.notesKey) data[tabConfig.footer.notesKey] = "";
    (tabConfig.footer.fields || []).forEach(function (f) { data[f.key] = ""; });
  }
  return data;
}

function get(data, defaultData, key, fallback) {
  if (data && data[key] !== undefined) return data[key];
  if (defaultData && defaultData[key] !== undefined) return defaultData[key];
  return fallback;
}

function ScorecardBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const scale = cfg.scale || { min: 1, max: 5 };
  const levels = [];
  for (let n = scale.min; n <= scale.max; n++) levels.push(n);
  const captureFields = (cfg.captureFields && cfg.captureFields.length) ? cfg.captureFields : ["note"];
  const gapThreshold = cfg.gapThreshold != null ? cfg.gapThreshold : cfg.gateThreshold;
  const [expandedId, setExpandedId] = useState(null);

  function colorFor(n) { return (scale.colors && scale.colors[String(n)]) || DEFAULT_SCALE_COLORS[n] || C.muted; }
  function entryFor(dimId) { return get(data, defaultData, dimId, {}); }
  function setField(dimId, field, value) {
    updateItem(dimId, function (prev) { return { ...(prev || {}), [field]: value }; });
  }

  const scoredCount = cfg.dimensions.filter(function (d) { return entryFor(d.id).score > 0; }).length;
  const scoreSum = cfg.dimensions.reduce(function (s, d) { return s + (entryFor(d.id).score || 0); }, 0);
  const avg = scoredCount > 0 ? (scoreSum / scoredCount).toFixed(1) : "-";
  const gapsTrackable = captureFields.includes("gap") && captureFields.includes("owner");
  const gapsAssigned = gapsTrackable ? cfg.dimensions.filter(function (d) { const e = entryFor(d.id); return e.gap && e.owner; }).length : null;

  function exportCsv() {
    const header = ["Dimension", "Score", "Label"];
    if (cfg.dimensions.some(function (d) { return d.expectedScore != null; })) header.push("Expected");
    if (captureFields.includes("presenter")) header.push("Presenter");
    if (captureFields.includes("owner")) header.push("Gap Owner");
    if (captureFields.includes("gap")) header.push("Key Gap");
    if (captureFields.includes("action")) header.push("Action");
    if (captureFields.includes("due")) header.push("Due");
    if (captureFields.includes("agreed")) header.push("Room Agreed");
    const rows = [header];
    cfg.dimensions.forEach(function (d) {
      const e = entryFor(d.id);
      const row = [d.label, e.score || "", e.score ? ((scale.labels && scale.labels[String(e.score)]) || "") : ""];
      if (header.indexOf("Expected") > -1) row.push(d.expectedScore != null ? d.expectedScore + " - " + (d.expectedLabel || "") : "");
      if (captureFields.includes("presenter")) row.push(e.presenter || "");
      if (captureFields.includes("owner")) row.push(e.owner || "");
      if (captureFields.includes("gap")) row.push(e.gap || "");
      if (captureFields.includes("action")) row.push(e.action || "");
      if (captureFields.includes("due")) row.push(e.due || "");
      if (captureFields.includes("agreed")) row.push(e.agreed ? "Yes" : "No");
      rows.push(row);
    });
    csvDownload(cfg.csvFilename, rows);
  }

  return (
    <div>
      {cfg.showSummaryStats !== false && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 8, marginBottom: 16 }}>
          <StatBox label="Dimensions scored" value={scoredCount + "/" + cfg.dimensions.length} color={scoredCount === cfg.dimensions.length ? C.green : C.orange} />
          <StatBox label="Avg score" value={avg} color={parseFloat(avg) >= (cfg.gateThreshold || 3) ? C.green : C.red} />
          {gapsAssigned != null && <StatBox label="Gaps assigned" value={gapsAssigned + "/" + cfg.dimensions.length} color={gapsAssigned === cfg.dimensions.length ? C.green : C.orange} />}
        </div>
      )}
      {cfg.dimensions.map(function (dim) {
        const entry = entryFor(dim.id);
        const score = entry.score || 0;
        const sc = score ? colorFor(score) : C.muted;
        const isOpen = expandedId === dim.id;
        const showGap = gapThreshold != null && score > 0 && score < gapThreshold;
        const hasExpandable = dim.facilitatorPrompt || dim.watchFor || (dim.questions && dim.questions.length) || dim.anchors;
        return (
          <div key={dim.id} style={{ background: C.white, border: (entry.agreed ? "2px solid " + C.green : "1px solid " + C.border), borderRadius: 8, padding: 14, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.dark }}>{dim.label}</div>
                {dim.phase && <div style={{ fontSize: 11, color: C.muted }}>{dim.phase}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: sc }}>{score || "-"}</div>
                {score > 0 && scale.labels && scale.labels[String(score)] && <div style={{ fontSize: 10, color: sc }}>{scale.labels[String(score)]}</div>}
              </div>
            </div>
            <ExpectedScoreBadge score={dim.expectedScore} label={dim.expectedLabel} colorFn={colorFor} />
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {levels.map(function (n) {
                const active = score === n;
                const nc = colorFor(n);
                return (
                  <button key={n} type="button" onClick={function () { setField(dim.id, "score", n); }}
                    style={{ flex: 1, padding: "8px 0", borderRadius: 4, border: active ? "2px solid " + nc : "1px solid " + C.border, background: active ? nc + "22" : C.white, color: nc, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    {n}
                  </button>
                );
              })}
            </div>
            {score > 0 && dim.anchors && dim.anchors[String(score)] && (
              <div style={{ background: C.light, borderRadius: 4, padding: "6px 10px", marginBottom: 10, fontSize: 11, color: C.text }}>
                <strong style={{ color: sc }}>{score}{scale.labels && scale.labels[String(score)] ? " \u2013 " + scale.labels[String(score)] : ""}:</strong> {dim.anchors[String(score)]}
              </div>
            )}
            {hasExpandable && (
              <div style={{ marginBottom: 8 }}>
                <button type="button" onClick={function () { setExpandedId(isOpen ? null : dim.id); }}
                  style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                  {isOpen ? "Hide" : "Show"} scoring questions and facilitator notes
                </button>
              </div>
            )}
            {isOpen && (
              <div style={{ marginBottom: 12 }}>
                <FacilitatorNote text={dim.facilitatorPrompt} />
                <WatchFor text={dim.watchFor} />
                {dim.questions && dim.questions.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.dark, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Scoring questions</div>
                    {dim.questions.map(function (q, i) {
                      return <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5, fontSize: 11, color: C.text }}><span style={{ color: C.muted, flexShrink: 0 }}>{i + 1}.</span><span>{q}</span></div>;
                    })}
                  </div>
                )}
                {dim.anchors && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.dark, marginTop: 10, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Score anchors</div>
                    {levels.map(function (n) {
                      if (!dim.anchors[String(n)]) return null;
                      return (
                        <div key={n} style={{ display: "flex", gap: 6, marginBottom: 3, fontSize: 11 }}>
                          <span style={{ color: colorFor(n), fontWeight: 700, width: 18, flexShrink: 0 }}>{n}</span>
                          <span style={{ color: C.text }}>{dim.anchors[String(n)]}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              {captureFields.includes("presenter") && (
                <div>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Presenter for this dimension</div>
                  <input value={entry.presenter || ""} onChange={function (e) { setField(dim.id, "presenter", e.target.value); }} placeholder="Who presents..."
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                </div>
              )}
              {captureFields.includes("owner") && (
                <div>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Gap Owner</div>
                  <input value={entry.owner || ""} onChange={function (e) { setField(dim.id, "owner", e.target.value); }} placeholder="Who closes this gap..."
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                </div>
              )}
            </div>
            {showGap && captureFields.includes("gap") && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Key Gap (score below {gapThreshold} - required)</div>
                <input value={entry.gap || ""} onChange={function (e) { setField(dim.id, "gap", e.target.value); }} placeholder="What specifically is missing or broken..."
                  style={{ width: "100%", padding: "6px 8px", border: "1px solid " + (entry.gap ? C.border : C.orange), borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                  {captureFields.includes("action") && (
                    <div>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Action to Close Gap</div>
                      <input value={entry.action || ""} onChange={function (e) { setField(dim.id, "action", e.target.value); }} placeholder="Specific action required..."
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                    </div>
                  )}
                  {captureFields.includes("due") && (
                    <div>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Due Date</div>
                      <input type="date" value={entry.due || ""} onChange={function (e) { setField(dim.id, "due", e.target.value); }}
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                    </div>
                  )}
                </div>
              </div>
            )}
            {captureFields.includes("note") && (
              <textarea value={entry.note || ""} onChange={function (e) { setField(dim.id, "note", e.target.value); }} placeholder="Notes..." rows={2}
                style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 11, boxSizing: "border-box", marginBottom: 8 }} />
            )}
            {captureFields.includes("agreed") && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={!!entry.agreed} onChange={function (e) { setField(dim.id, "agreed", e.target.checked); }} style={{ cursor: "pointer" }} />
                <label style={{ fontSize: 12, color: entry.agreed ? C.green : C.muted, fontWeight: entry.agreed ? 700 : 400, cursor: "pointer" }}
                  onClick={function () { setField(dim.id, "agreed", !entry.agreed); }}>Room agreed on this score</label>
              </div>
            )}
          </div>
        );
      })}
      {cfg.csvFilename && (
        <button type="button" onClick={exportCsv} style={{ padding: "8px 16px", background: C.white, color: C.dark, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontSize: 12, marginTop: 4 }}>
          Export CSV
        </button>
      )}
    </div>
  );
}

function GateChecklistBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const tristate = cfg.statusMode === "tristate";
  const tristateOptions = (cfg.tristateOptions && cfg.tristateOptions.length) ? cfg.tristateOptions : ["not-met", "in-progress", "met"];
  const metValue = cfg.metValue || "met";
  const items = cfg.items || [];
  function keyFor(item) { return "gate_" + item.id; }
  function entryFor(item) { return get(data, defaultData, keyFor(item), tristate ? { status: tristateOptions[0] } : {}); }
  function setField(item, field, value) {
    updateItem(keyFor(item), function (prev) { return { ...(prev || {}), [field]: value }; });
  }
  const metCount = tristate ? items.filter(function (item) { return (entryFor(item).status || tristateOptions[0]) === metValue; }).length : 0;
  const allMet = tristate && items.length > 0 && metCount === items.length;

  return (
    <div>
      {tristate && cfg.showSummary && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: allMet ? C.green : C.orange }}>{metCount}/{items.length} gates met</span>
        </div>
      )}
      {items.map(function (item) {
        const entry = entryFor(item);
        if (tristate) {
          const status = entry.status || tristateOptions[0];
          const bg = status === metValue ? "#d1fae5" : status === tristateOptions[1] ? C.yellowBg : "#fee2e2";
          return (
            <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "8px 10px", background: bg, borderRadius: 6 }}>
              <select value={status} onChange={function (e) { setField(item, "status", e.target.value); }}
                style={{ padding: "3px 8px", fontSize: 11, border: "1px solid " + C.border, borderRadius: 4, background: C.white, color: C.dark, flexShrink: 0 }}>
                {tristateOptions.map(function (o) { return <option key={o} value={o}>{o}</option>; })}
              </select>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: C.dark, lineHeight: 1.5, fontWeight: 600 }}>{item.label}</div>
                {item.detail && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{item.detail}</div>}
                {item.phase && <div style={{ fontSize: 10, color: C.blue, marginTop: 2, fontWeight: 700 }}>Phase: {item.phase}</div>}
              </div>
            </div>
          );
        }
        return (
          <div key={item.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid " + C.border }}>
            <input type="checkbox" checked={!!entry.met} onChange={function (e) { setField(item, "met", e.target.checked); }} style={{ marginTop: 2, cursor: "pointer" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: entry.met ? C.green : C.dark, fontWeight: entry.met ? 700 : 400 }}>{item.label}</div>
              {item.defaultOwner && <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>Default owner: {item.defaultOwner}</div>}
              <input value={entry.notes || ""} onChange={function (e) { setField(item, "notes", e.target.value); }} placeholder={cfg.notesPlaceholder || "Notes or conditions..."}
                style={{ marginTop: 4, width: "100%", padding: "4px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 11, boxSizing: "border-box" }} />
            </div>
          </div>
        );
      })}
      {tristate && cfg.showSummary && allMet && cfg.allMetBannerText && (
        <div style={{ marginTop: 10, padding: "8px 12px", background: "#d1fae5", borderRadius: 6, fontSize: 12, color: "#065f46", fontWeight: 700 }}>
          {cfg.allMetBannerText}
        </div>
      )}
    </div>
  );
}

function MatrixBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const extraKey = "_extraRows_" + block.id;
  const extraRows = get(data, defaultData, extraKey, []);
  const allRows = (cfg.rows || []).concat(extraRows);

  function getCells(rowId) { return get(data, defaultData, rowId, {}); }
  function setCell(rowId, colId, value) {
    updateItem(rowId, function (prev) { return { ...(prev || {}), [colId]: value }; });
  }
  function addRow() {
    const label = window.prompt("New " + (cfg.rowLabel || "row") + " name:");
    if (!label) return;
    const id = "row-" + Date.now();
    updateItem(extraKey, function (prev) { return (prev || []).concat([{ id: id, label: label }]); });
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid " + C.border, fontSize: 10, textTransform: "uppercase", color: C.muted }}>{cfg.rowLabel}</th>
            {(cfg.columns || []).map(function (col) {
              return <th key={col.id} style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid " + C.border, fontSize: 10, textTransform: "uppercase", color: C.muted }}>{col.label}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {allRows.map(function (row) {
            const cellData = getCells(row.id);
            return (
              <tr key={row.id}>
                <td style={{ padding: 8, borderBottom: "1px solid " + C.border, fontWeight: 700, color: C.dark, verticalAlign: "top" }}>{row.label}</td>
                {(cfg.columns || []).map(function (col) {
                  return (
                    <td key={col.id} style={{ padding: 8, borderBottom: "1px solid " + C.border, verticalAlign: "top", minWidth: 140 }}>
                      <FieldInput field={col} value={cellData[col.id]} onChange={function (v) { setCell(row.id, col.id, v); }} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {cfg.allowAddRows && (
        <button type="button" onClick={addRow} style={{ marginTop: 8, fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
          + Add {cfg.rowLabel || "row"}
        </button>
      )}
    </div>
  );
}

// Shared by ListBlock and TrackerBlock -- one implementation so the two
// block types' stats[]/groupByKey behavior can't drift apart from each
// other (they reference the same schema definitions; this keeps the
// renderer side just as unified).
function statTileColor(stat, count, total) {
  const complete = stat.invertColor ? count === 0 : count === total;
  return stat.color || (complete ? C.green : (stat.invertColor ? C.red : C.orange));
}

function StatTileBox({ label, count, total, showTotal, color }) {
  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: color }}>{showTotal ? count + "/" + total : count}</div>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

// Block-scoped: all stats source from the same items/getItemData (a
// block's own list.stats/tracker.stats).
function StatTilesRow({ stats, items, getItemData }) {
  if (!stats || stats.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
      {stats.map(function (stat) {
        const { count, total } = computeStatTile(items, stat, getItemData);
        return <StatTileBox key={stat.label} label={stat.label} count={count} total={total} showTotal={stat.showTotal !== false} color={statTileColor(stat, count, total)} />;
      })}
    </div>
  );
}

// Tab-scoped: each stat can source from a DIFFERENT block (tabConfig.stats[],
// via sourceBlockId), e.g. S14's "Commitments locked" + "Action items locked"
// combined into one shared row above every block. Shares StatTileBox/
// statTileColor with StatTilesRow above -- only how items/getItemData are
// sourced differs.
function TabStatsRow({ stats, tabConfig, data, defaultData }) {
  if (!stats || stats.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
      {stats.map(function (stat) {
        const block = (tabConfig.blocks || []).find(function (b) { return b.id === stat.sourceBlockId; });
        const { items, getItemData } = getBlockItemsAndAccessor(block, data, defaultData);
        const { count, total } = computeStatTile(items, stat, getItemData);
        return <StatTileBox key={stat.label} label={stat.label} count={count} total={total} showTotal={stat.showTotal !== false} color={statTileColor(stat, count, total)} />;
      })}
    </div>
  );
}

function GroupFilterTabs({ groupByKey, items, value, onChange }) {
  if (!groupByKey) return null;
  const groupValues = Array.from(new Set(items.map(function (it) { return it[groupByKey]; }).filter(Boolean)));
  if (groupValues.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
      {["all"].concat(groupValues).map(function (val) {
        const count = val === "all" ? items.length : items.filter(function (it) { return it[groupByKey] === val; }).length;
        return (
          <button key={val} type="button" onClick={function () { onChange(val); }}
            style={{ padding: "4px 12px", fontSize: 11, borderRadius: 4, border: "1px solid " + C.border, background: value === val ? C.dark : C.white, color: value === val ? C.white : C.text, cursor: "pointer", fontWeight: value === val ? 700 : 400 }}>
            {val} ({count})
          </button>
        );
      })}
    </div>
  );
}

function ListBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const items = cfg.items || [];
  const [groupFilter, setGroupFilter] = useState("all");

  function setField(itemId, fieldId, value) {
    updateItem(itemId, function (prev) { return { ...(prev || {}), [fieldId]: value }; });
  }
  function getItemData(item) {
    return get(data, defaultData, item.id, {});
  }

  const visibleItems = (cfg.groupByKey && groupFilter !== "all")
    ? items.filter(function (it) { return it[cfg.groupByKey] === groupFilter; })
    : items;

  return (
    <div>
      <StatTilesRow stats={cfg.stats} items={items} getItemData={getItemData} />
      <GroupFilterTabs groupByKey={cfg.groupByKey} items={items} value={groupFilter} onChange={setGroupFilter} />
      {visibleItems.map(function (item) {
        const itemData = get(data, defaultData, item.id, {});
        return (
          <div key={item.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid " + C.border }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.dark }}>{item.label}</div>
            {item.badges && item.badges.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 4, marginBottom: 4, flexWrap: "wrap" }}>
                {item.badges.map(function (b, i) {
                  return <span key={i} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: (b.color || C.muted) + "22", color: b.color || C.muted, fontWeight: 700 }}>{b.label}</span>;
                })}
              </div>
            )}
            {item.description && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{item.description}</div>}
            {item.referenceNote && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontStyle: "italic" }}>{item.referenceNote}</div>}
            {item.measureNote && <div style={{ fontSize: 10, color: C.blue, marginBottom: 4 }}>{item.measureNote}</div>}
            {item.targetBadge && <div style={{ fontSize: 11, background: C.dark, color: C.gold, padding: "2px 7px", borderRadius: 3, fontWeight: 700, display: "inline-block", marginBottom: 8 }}>Target: {item.targetBadge}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {(cfg.fields || []).map(function (field) {
                const placeholderOverride = field.placeholderFromItemKey ? item[field.placeholderFromItemKey] : undefined;
                return (
                  <div key={field.id} style={field.inputType === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{field.label}</div>
                    <FieldInput field={field} value={itemData[field.id]} onChange={function (v) { setField(item.id, field.id, v); }} placeholderOverride={placeholderOverride} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NarrativeBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const entriesKey = cfg.storageKey || ("_entries_" + block.id);
  const entries = get(data, defaultData, entriesKey, []);
  const compact = cfg.layout === "compact";

  function setEntryField(entryId, fieldId, value) {
    updateItem(entriesKey, function (prev) {
      return (prev || []).map(function (e) { return e.id === entryId ? { ...e, [fieldId]: value } : e; });
    });
  }
  function addEntry() {
    const id = "entry-" + Date.now();
    updateItem(entriesKey, function (prev) { return (prev || []).concat([{ id: id }]); });
  }
  function removeEntry(entryId) {
    updateItem(entriesKey, function (prev) { return (prev || []).filter(function (e) { return e.id !== entryId; }); });
  }

  return (
    <div>
      {cfg.intro && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{cfg.intro}</div>}
      {cfg.liveCount && (
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 10 }}>
          {entries.filter(function (e) { return !!e[cfg.liveCount.field]; }).length} {cfg.liveCount.label}
        </div>
      )}
      {entries.map(function (entry, idx) {
        if (compact) {
          return (
            <div key={entry.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid " + C.border }}>
              {(cfg.fields || []).map(function (field) {
                return (
                  <div key={field.id} style={(field.inputType === "text" || field.inputType === "textarea") ? { flex: 1 } : undefined}>
                    <FieldInput field={field} value={entry[field.id]} onChange={function (v) { setEntryField(entry.id, field.id, v); }} />
                  </div>
                );
              })}
              {cfg.allowMultipleEntries !== false && (
                <button type="button" onClick={function () { removeEntry(entry.id); }} style={{ padding: "4px 8px", border: "1px solid " + C.border, borderRadius: 4, background: C.white, color: C.red, cursor: "pointer", fontSize: 12 }}>✕</button>
              )}
            </div>
          );
        }
        return (
          <div key={entry.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid " + C.border }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>{(cfg.entryLabel || "Entry") + " " + (idx + 1)}</div>
              {cfg.allowMultipleEntries !== false && (
                <button type="button" onClick={function () { removeEntry(entry.id); }} style={{ fontSize: 11, color: C.red, background: "none", border: "none", cursor: "pointer" }}>Remove</button>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {(cfg.fields || []).map(function (field) {
                return (
                  <div key={field.id} style={field.inputType === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{field.label}</div>
                    <FieldInput field={field} value={entry[field.id]} onChange={function (v) { setEntryField(entry.id, field.id, v); }} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {(cfg.allowMultipleEntries !== false || entries.length === 0) && (
        <button type="button" onClick={addEntry} style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
          + Add {cfg.entryLabel || "entry"}
        </button>
      )}
    </div>
  );
}

function fieldOptionColor(fields, fieldId, value) {
  const field = (fields || []).find(function (f) { return f.id === fieldId; });
  if (!field || !field.options) return null;
  const opt = field.options.find(function (o) { return typeof o === "object" && o.value === value; });
  return opt ? opt.color : null;
}

// v1/v2 switch: the PRESENCE of cfg.fields is the documented, intentional
// switch between the original always-visible flat status(+phase) row
// (v1, unchanged) and click-to-expand rows with a full field grid (v2,
// built for S10). See trackerBlock's schema description for the full
// rationale -- this is a deliberate design choice, not an implicit
// side effect of adding fields.
function TrackerBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const items = cfg.items || [];
  const expandable = !!(cfg.fields && cfg.fields.length > 0);
  const [groupFilter, setGroupFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  function getItemData(item) {
    return get(data, defaultData, item.id, {});
  }
  function setField(itemId, fieldId, value) {
    updateItem(itemId, function (prev) { return { ...(prev || {}), [fieldId]: value }; });
  }

  const visibleItems = (cfg.groupByKey && groupFilter !== "all")
    ? items.filter(function (it) { return it[cfg.groupByKey] === groupFilter; })
    : items;

  function exportCsv() {
    const header = [cfg.itemLabel || "Item"];
    if (cfg.groupByKey) header.push(cfg.groupByKey);
    else if (cfg.phaseOptions) header.push("Phase");
    header.push("Status");
    (cfg.fields || []).forEach(function (f) { if (f.id !== "status") header.push(f.label); });
    const rows = [header];
    items.forEach(function (item) {
      const itemData = get(data, defaultData, item.id, {});
      const row = [item.label];
      if (cfg.groupByKey) row.push(item[cfg.groupByKey] || "");
      else if (cfg.phaseOptions) row.push(itemData.phase || "");
      row.push(itemData.status || "");
      (cfg.fields || []).forEach(function (f) { if (f.id !== "status") row.push(itemData[f.id] || ""); });
      rows.push(row);
    });
    csvDownload(cfg.csvFilename, rows);
  }

  return (
    <div>
      <StatTilesRow stats={cfg.stats} items={items} getItemData={getItemData} />
      <GroupFilterTabs groupByKey={cfg.groupByKey} items={items} value={groupFilter} onChange={setGroupFilter} />
      {!expandable && visibleItems.map(function (item) {
        const itemData = get(data, defaultData, item.id, {});
        return (
          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid " + C.border }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.dark }}>{item.label}</div>
              {item.description && <div style={{ fontSize: 11, color: C.muted }}>{item.description}</div>}
            </div>
            <select value={itemData.status || ""} onChange={function (e) { setField(item.id, "status", e.target.value); }}
              style={{ fontSize: 11, padding: "4px 6px", border: "1px solid " + C.border, borderRadius: 4 }}>
              <option value="">Status...</option>
              {(cfg.statusOptions || []).map(function (s) { return <option key={s} value={s}>{s}</option>; })}
            </select>
            {cfg.phaseOptions && (
              <select value={itemData.phase || ""} onChange={function (e) { setField(item.id, "phase", e.target.value); }}
                style={{ fontSize: 11, padding: "4px 6px", border: "1px solid " + C.border, borderRadius: 4 }}>
                <option value="">Phase...</option>
                {cfg.phaseOptions.map(function (p) { return <option key={p} value={p}>{p}</option>; })}
              </select>
            )}
          </div>
        );
      })}
      {expandable && visibleItems.map(function (item) {
        const itemData = get(data, defaultData, item.id, {});
        const isOpen = expanded === item.id;
        const statusColor = fieldOptionColor(cfg.fields, "status", itemData.status) || C.muted;
        return (
          <div key={item.id} style={{ background: C.white, borderTop: "1px solid " + C.border, borderRight: "1px solid " + C.border, borderBottom: "1px solid " + C.border, borderLeft: "4px solid " + (item.accentColor || C.border), borderRadius: 6, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }} onClick={function () { setExpanded(isOpen ? null : item.id); }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.dark }}>{item.label}</span>
                  {(cfg.computedBadges || []).map(function (b, i) {
                    return evaluateFieldPredicate(b, itemData) ? (
                      <span key={i} style={{ fontSize: 10, background: (b.color || C.muted) + "22", color: b.color || C.muted, padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>{b.label}</span>
                    ) : null;
                  })}
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.muted }}>
                  {(cfg.summaryFields || []).map(function (fieldId) {
                    const field = cfg.fields.find(function (f) { return f.id === fieldId; });
                    return (
                      <span key={fieldId}>{(field && field.label) || fieldId}: <strong style={{ color: C.dark }}>{itemData[fieldId] || "—"}</strong></span>
                    );
                  })}
                  <span style={{ color: statusColor, fontWeight: 600 }}>{itemData.status || ""}</span>
                </div>
              </div>
              <span style={{ color: C.muted, marginLeft: 8 }}>{isOpen ? "▲" : "▼"}</span>
            </div>
            {isOpen && (
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {cfg.fields.map(function (field) {
                  return (
                    <div key={field.id} style={field.inputType === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{field.label}</div>
                      <FieldInput field={field} value={itemData[field.id]} onChange={function (v) { setField(item.id, field.id, v); }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {cfg.csvFilename && (
        <button type="button" onClick={exportCsv} style={{ padding: "8px 16px", background: C.white, color: C.dark, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontSize: 12, marginTop: 4 }}>
          Export CSV
        </button>
      )}
    </div>
  );
}

const DECISION_FIELD_VOCAB = {
  optionsConsidered: { label: "Options Considered", inputType: "textarea" },
  chosen: { label: "Chosen", inputType: "text", isSelection: true },
  selected: { label: "Decision Selected", inputType: "text", isSelection: true },
  rationale: { label: "Rationale", inputType: "textarea" },
  owner: { label: "Owner", inputType: "text" },
  date: { label: "Date", inputType: "date" },
  targetDate: { label: "Target Date", inputType: "date" },
  status: { label: "Status", inputType: "select", options: DECISION_STATUSES.map(function (s) { return s.value; }) },
  decision: { label: "Decision", inputType: "textarea" },
  notes: { label: "Notes / Caveats", inputType: "text" },
};

const COMMITMENT_FIELD_VOCAB = {
  commitment: { label: "Commitment", inputType: "text" },
  owner: { label: "Owner", inputType: "owner" },
  dueDate: { label: "Due Date", inputType: "date" },
  status: { label: "Status", inputType: "select", options: ["Not Started", "In Progress", "Done", "At Risk"] },
  due: { label: "By When", inputType: "select", options: DUE_DATES },
  locked: { label: "Locked", inputType: "checkbox" },
};
const CURRENT_STATE_FIELD_VOCAB = {
  acknowledged: { label: "Room acknowledged", inputType: "checkbox" },
  current: { label: "Current State (captured live)", inputType: "textarea" },
  gaps: { label: "Gaps and What Needs to Change", inputType: "textarea" },
};

function AreaFieldsBlock({ block, data, defaultData, updateItem, fieldVocab }) {
  const cfg = block.config;
  const captureFields = cfg.captureFields && cfg.captureFields.length ? cfg.captureFields : Object.keys(fieldVocab);
  const boxed = !!cfg.boxed;
  const [expandedContext, setExpandedContext] = useState(null);
  function setField(areaId, fieldKey, value) {
    updateItem(areaId, function (prev) { return { ...(prev || {}), [fieldKey]: value }; });
  }
  return (
    <div>
      {(cfg.areas || []).map(function (area) {
        const areaData = get(data, defaultData, area.id, {});
        const isCtx = expandedContext === area.id;
        const liveBorderColor = area.borderColor && evaluateFieldPredicate(area.borderColor, areaData) ? area.borderColor.color : null;
        const containerStyle = boxed
          ? { background: C.white, border: "1px solid " + (liveBorderColor || C.border), borderRadius: 8, padding: "14px 16px", marginBottom: 12 }
          : { marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid " + C.border };
        return (
          <div key={area.id} style={containerStyle}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.dark }}>{area.label}</div>
            {area.description && <div style={{ fontSize: 11, color: C.blue, marginBottom: 6, fontStyle: "italic" }}>{area.description}</div>}
            {area.keyQuestion && (
              <div style={{ background: "#f0f7ff", borderRadius: 4, padding: "6px 10px", marginBottom: 8, fontSize: 11 }}>
                <span style={{ fontWeight: 700, color: C.blue }}>Key question: </span>
                <span style={{ color: C.text }}>{area.keyQuestion}</span>
              </div>
            )}
            {area.context && (
              <div style={{ marginBottom: 8 }}>
                <button type="button" onClick={function () { setExpandedContext(isCtx ? null : area.id); }}
                  style={{ fontSize: 11, color: C.muted, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                  {isCtx ? "Hide" : "Show"} facilitator context
                </button>
                {isCtx && (
                  <div style={{ marginTop: 6, background: C.light, borderRadius: 4, padding: "6px 10px", fontSize: 11, color: C.text }}>{area.context}</div>
                )}
              </div>
            )}
            {area.recommended && (
              <div style={{ background: C.light, borderRadius: 6, padding: "8px 12px", marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3, fontWeight: 700 }}>
                  Recommendation: <span style={{ color: C.gold }}>{area.recommended}</span>
                </div>
                {area.recommendationReason && <div style={{ fontSize: 11, color: C.text, fontStyle: "italic" }}>{area.recommendationReason}</div>}
              </div>
            )}
            {area.considerations && area.considerations.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {area.considerations.map(function (c, i) {
                  return <div key={i} style={{ fontSize: 12, color: C.text, marginBottom: 4, paddingLeft: 10, borderLeft: "2px solid " + C.border, lineHeight: 1.5 }}>{c}</div>;
                })}
              </div>
            )}
            {area.blocker && (
              <div style={{ background: "#fef2f2", borderRadius: 6, padding: "6px 10px", marginBottom: 8, fontSize: 11, color: C.red }}>
                <strong>Blocker:</strong> {area.blocker}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {captureFields.map(function (fieldKey) {
                let fdef = fieldVocab[fieldKey] || { label: fieldKey, inputType: "text" };
                if (fdef.isSelection && area.options && area.options.length) {
                  fdef = Object.assign({}, fdef, { inputType: "select", options: area.options });
                }
                return (
                  <div key={fieldKey} style={(fdef.inputType === "textarea" || fdef.inputType === "checkbox") ? { gridColumn: "1 / -1" } : undefined}>
                    {fdef.inputType !== "checkbox" && <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{fdef.label}</div>}
                    <FieldInput field={fdef} value={areaData[fieldKey]} onChange={function (v) { setField(area.id, fieldKey, v); }} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RankedScorecardBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const dims = cfg.dimensions || [];
  const items = cfg.items || [];
  const [view, setView] = useState("score");
  const [showGuide, setShowGuide] = useState(false);
  const confirmedKey = "_confirmed_" + block.id;
  const confirmedNotesKey = "_confirmedNotes_" + block.id;

  function entryFor(id) { return get(data, defaultData, id, {}); }
  function setField(id, field, val) {
    updateItem(id, function (prev) { return { ...(prev || {}), [field]: val }; });
  }
  function tot(id) {
    const e = entryFor(id);
    return dims.reduce(function (s, d) { return s + (e[d.id] || 0); }, 0);
  }
  function expectedTot(it) {
    return dims.reduce(function (s, d) { return s + ((it.expectedScores && it.expectedScores[d.id]) || 0); }, 0);
  }
  const ranked = items.slice().sort(function (a, b) { return tot(b.id) - tot(a.id); });
  const topN = cfg.topHighlightCount != null ? cfg.topHighlightCount : 4;
  const confirmedItem = items.find(function (it) { return it.confirmed; });
  const scoredCount = items.filter(function (it) { return tot(it.id) > 0; }).length;
  const phaseCount = items.filter(function (it) { return entryFor(it.id).phase; }).length;
  const ownerCount = items.filter(function (it) { return entryFor(it.id).owner; }).length;
  const confirmed = !!get(data, defaultData, confirmedKey, false);

  function exportCsv() {
    const header = ["Rank", "Item", "Category"].concat(dims.map(function (d) { return d.label; })).concat(["Total", "Expected Total", "Phase", "Owner", "Notes", "Data Dependencies"]);
    const rows = [header];
    ranked.forEach(function (it, i) {
      const e = entryFor(it.id);
      const t = tot(it.id);
      rows.push([t ? i + 1 : "", it.name, it.category || ""].concat(dims.map(function (d) { return e[d.id] || ""; })).concat([t || "", expectedTot(it), e.phase || "", e.owner || "", e.notes || e.deferred_reason || "", e.dataDeps || it.dependencyReference || ""]));
    });
    csvDownload(cfg.csvFilename || "export.csv", rows);
  }

  const badgeStyle = { fontSize: 10, padding: "1px 6px", borderRadius: 10, fontWeight: 700 };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 8, marginBottom: 16 }}>
        {confirmedItem && <StatBox label="Confirmed pilot" value={confirmedItem.name} color={C.gold} />}
        <StatBox label="Scored" value={scoredCount + "/" + items.length} />
        <StatBox label="Phase assigned" value={phaseCount + "/" + items.length} color={C.blue} />
        <StatBox label="Owners assigned" value={ownerCount + "/" + items.length} color={C.green} />
      </div>
      {dims.some(function (d) { return d.description; }) && (
        <div style={{ marginBottom: 14 }}>
          <button onClick={function () { setShowGuide(!showGuide); }}
            style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            {showGuide ? "Hide" : "Show"} scoring framework guide
          </button>
          {showGuide && (
            <div style={{ marginTop: 8, background: C.light, borderRadius: 8, padding: 12 }}>
              {dims.map(function (d) {
                return (
                  <div key={d.id} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: d.color || C.dark, marginBottom: 2 }}>{d.label}</div>
                    <div style={{ fontSize: 11, color: C.text }}>{d.description}</div>
                  </div>
                );
              })}
              {cfg.watchForText && <WatchFor text={cfg.watchForText} />}
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {["score", "assign", "deps"].map(function (v) {
          return (
            <button key={v} onClick={function () { setView(v); }}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid " + C.border, background: view === v ? C.dark : C.white, color: view === v ? C.gold : C.muted, fontSize: 12, fontWeight: view === v ? 700 : 400, cursor: "pointer" }}>
              {v === "score" ? "Score & Rank" : v === "assign" ? "Assign & Phase" : "Data Dependencies"}
            </button>
          );
        })}
      </div>
      {view === "score" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {ranked.map(function (it, ri) {
            const e = entryFor(it.id);
            const t = tot(it.id);
            const isTop = !it.confirmed && ri < topN && t > 0;
            return (
              <Card key={it.id} style={{ border: it.confirmed ? "2px solid " + C.gold : isTop ? "2px solid " + C.green : "1px solid " + C.border }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  {t > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, width: 20 }}>#{ri + 1}</span>}
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.dark, flex: 1 }}>{it.name}</span>
                  {it.confirmed && <span style={{ ...badgeStyle, background: "#fef9c3", color: "#854d0e" }}>confirmed</span>}
                  {isTop && <span style={{ ...badgeStyle, background: "#d1fae5", color: "#065f46" }}>top</span>}
                  <span style={{ fontSize: 13, fontWeight: 700, color: t > 0 ? C.dark : C.muted }}>{t || "-"}</span>
                </div>
                {it.category && <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{it.category}</div>}
                {it.note && <div style={{ fontSize: 10, color: C.blue, marginBottom: 6, fontStyle: "italic" }}>{it.note}</div>}
                {it.expectedScores && <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>Expected: {dims.map(function (d) { return d.label + " " + (it.expectedScores[d.id] || 0); }).join(" / ")} = {expectedTot(it)}</div>}
                {dims.map(function (d) {
                  return (
                    <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: d.color || C.dark, width: 72, flexShrink: 0 }}>{d.label}</span>
                      <div style={{ display: "flex", gap: 3 }}>
                        {[1, 2, 3, 4, 5].map(function (n) {
                          return (
                            <button key={n} disabled={it.confirmed} onClick={function () { setField(it.id, d.id, n); }}
                              style={{ width: 26, height: 26, borderRadius: 4, border: e[d.id] === n ? "2px solid " + (d.color || C.dark) : "1px solid " + C.border, background: e[d.id] === n ? (d.color || C.dark) + "22" : C.white, color: e[d.id] === n ? (d.color || C.dark) : C.muted, fontWeight: 700, fontSize: 12, cursor: it.confirmed ? "default" : "pointer" }}>
                              {n}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </Card>
            );
          })}
        </div>
      )}
      {view === "assign" && (
        <div>
          {cfg.assignBanner && (
            <div style={{ background: "#fffdf5", border: "1px solid " + C.gold, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#78350f" }}>
              {cfg.assignBanner}
            </div>
          )}
          {ranked.map(function (it, ri) {
            const e = entryFor(it.id);
            const t = tot(it.id);
            return (
              <Card key={it.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>{it.name}</span>
                    {t > 0 && <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>Score: {t} (#{ri + 1})</span>}
                  </div>
                  {it.confirmed && <span style={{ ...badgeStyle, background: "#fef9c3", color: "#854d0e" }}>confirmed pilot</span>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Phase Assignment</div>
                    <select value={e.phase || ""} onChange={function (ev) { setField(it.id, "phase", ev.target.value); }}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12 }}>
                      <option value="">Select phase...</option>
                      {(cfg.phaseOptions || []).map(function (p) { return <option key={p} value={p}>{p}</option>; })}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Business Owner</div>
                    <input value={e.owner || ""} onChange={function (ev) { setField(it.id, "owner", ev.target.value); }} placeholder="Assign owner..."
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{e.phase === "Deferred" ? "Reason Deferred" : "Notes"}</div>
                    <input value={e.phase === "Deferred" ? (e.deferred_reason || "") : (e.notes || "")}
                      onChange={function (ev) { setField(it.id, e.phase === "Deferred" ? "deferred_reason" : "notes", ev.target.value); }}
                      placeholder={e.phase === "Deferred" ? "Why deferred..." : "Notes..."}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                  </div>
                </div>
              </Card>
            );
          })}
          {cfg.confirmationCard && (
            <Card style={{ border: "1px solid " + C.gold }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.dark, marginBottom: 8 }}>{cfg.confirmationCard.title}</div>
              {cfg.confirmationCard.facilitatorNote && <FacilitatorNote text={cfg.confirmationCard.facilitatorNote} />}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <input type="checkbox" checked={confirmed} onChange={function (ev) { updateItem(confirmedKey, ev.target.checked); }} style={{ cursor: "pointer" }} />
                <label style={{ fontSize: 12, fontWeight: 700, color: confirmed ? C.green : C.muted, cursor: "pointer" }}>{cfg.confirmationCard.checkboxLabel || "Confirmed"}</label>
              </div>
              <textarea value={get(data, defaultData, confirmedNotesKey, "")} onChange={function (ev) { updateItem(confirmedNotesKey, ev.target.value); }}
                placeholder={cfg.confirmationCard.notesPlaceholder || ""} rows={3}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
            </Card>
          )}
        </div>
      )}
      {view === "deps" && (
        <div>
          {cfg.depsFacilitatorNote && <FacilitatorNote text={cfg.depsFacilitatorNote} />}
          {ranked.filter(function (it) { return tot(it.id) > 0 || it.confirmed; }).map(function (it) {
            const e = entryFor(it.id);
            return (
              <Card key={it.id}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.dark, marginBottom: 4 }}>{it.name}</div>
                {it.dependencyReference && (
                  <div style={{ background: C.light, borderRadius: 4, padding: "6px 10px", fontSize: 11, color: C.text, marginBottom: 8 }}>
                    <strong style={{ color: C.blue }}>From platform plan: </strong>{it.dependencyReference}
                  </div>
                )}
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{cfg.depsFieldLabel || "Additional dependencies"}</div>
                <textarea value={e.dataDeps || ""} onChange={function (ev) { setField(it.id, "dataDeps", ev.target.value); }}
                  placeholder={cfg.depsFieldPlaceholder || ""} rows={2}
                  style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
              </Card>
            );
          })}
        </div>
      )}
      {cfg.csvFilename && (
        <button onClick={exportCsv} style={{ padding: "8px 16px", background: C.white, color: C.dark, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontSize: 12, marginTop: 8 }}>
          Export CSV
        </button>
      )}
    </div>
  );
}

function SettingsCardBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const cardData = get(data, defaultData, cfg.dataKey, {});
  function setField(key, value) {
    updateItem(cfg.dataKey, function (prev) { return { ...(prev || {}), [key]: value }; });
  }
  const colorStyles = {
    plain: { bg: "#fff", border: C.border, label: C.dark },
    green: { bg: "#f0fdf4", border: "#86efac", label: "#065f46" },
    blue: { bg: "#eff6ff", border: "#93c5fd", label: "#1e40af" },
    orange: { bg: "#fff7ed", border: "#f97316", label: "#c2410c" },
  };
  const status = cardData.status != null ? cardData.status : (cfg.defaultStatus || "");
  return (
    <div style={{ background: "#fffdf0", border: "2px solid " + C.gold, borderRadius: 8, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.dark }}>{block.title}</div>
          {cfg.subtitle && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{cfg.subtitle}</div>}
        </div>
        {status && (
          <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 12, background: "#fef3c7", color: "#92400e", fontWeight: 700, whiteSpace: "nowrap" }}>{status}</span>
        )}
      </div>
      {cfg.calloutText && (
        <div style={{ background: "#f0f7ff", border: "1px solid " + C.blue, borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 11, color: C.dark }}>
          <span style={{ fontWeight: 700, color: C.blue }}>{cfg.calloutLabel || "Why this is here:"} </span>{cfg.calloutText}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {(cfg.fields || []).map(function (f) {
          const cs = colorStyles[f.color] || colorStyles.plain;
          return (
            <div key={f.key} style={{ background: cs.bg, border: "1px solid " + cs.border, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: cs.label, marginBottom: 2 }}>{f.label}</div>
              {f.helperText && <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>{f.helperText}</div>}
              <textarea value={cardData[f.key] || ""} onChange={function (e) { setField(f.key, e.target.value); }} rows={f.rows || 3}
                style={{ width: "100%", padding: "6px 8px", border: "1px solid " + cs.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", background: "#fff" }} />
            </div>
          );
        })}
      </div>
      {cfg.footerFields && cfg.footerFields.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))", gap: 10 }}>
          {cfg.footerFields.map(function (f) {
            const v = cardData[f.key] != null ? cardData[f.key] : (f.defaultValue != null ? f.defaultValue : "");
            return (
              <div key={f.key}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{f.label}</div>
                {f.inputType === "select" ? (
                  <select value={v} onChange={function (e) { setField(f.key, e.target.value); }}
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }}>
                    {(f.options || []).map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
                  </select>
                ) : (
                  <input value={v} onChange={function (e) { setField(f.key, e.target.value); }} placeholder={f.placeholder || ""}
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StoryTrackerBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  const items = cfg.items || [];
  const statusOptions = cfg.statusOptions && cfg.statusOptions.length ? cfg.statusOptions : ["not-started", "in-progress", "confirmed"];
  const [view, setView] = useState("overview");
  const [expanded, setExpanded] = useState(null);

  function entryFor(id) { return get(data, defaultData, id, {}); }
  function setField(id, key, value) {
    updateItem(id, function (prev) { return { ...(prev || {}), [key]: value }; });
  }
  function cycleStatus(id) {
    const cur = entryFor(id).status || statusOptions[0];
    const idx = statusOptions.indexOf(cur);
    const next = statusOptions[(idx + 1) % statusOptions.length];
    setField(id, "status", next);
  }
  function statusStyle(s) {
    return s === "confirmed" ? { bg: "#d1fae5", color: "#065f46" } : s === "in-progress" ? { bg: "#fef3c7", color: "#92400e" } : { bg: "#f3f4f6", color: "#6b7280" };
  }
  const priorityItems = items.filter(function (it) { return it.inPriorityView; });

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {["overview", "data", "priority"].map(function (v) {
          return (
            <button key={v} onClick={function () { setView(v); }}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid " + C.border, background: view === v ? C.dark : C.white, color: view === v ? C.gold : C.muted, fontSize: 12, fontWeight: view === v ? 700 : 400, cursor: "pointer" }}>
              {v === "overview" ? "Overview" : v === "data" ? "Data" : (cfg.priorityHeaderLabel || "Priority")}
            </button>
          );
        })}
      </div>

      {view === "overview" && (
        <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "28px 1fr 1fr 1fr 110px", gap: 8, padding: "8px 12px", background: "#f3f4f6", fontSize: 11, fontWeight: 700, color: C.muted }}>
            <span>#</span><span>Item</span><span>Business Owner</span><span>BI Build Owner</span><span>Status</span>
          </div>
          {items.map(function (it) {
            const e = entryFor(it.id);
            const isP2 = !!it.inPriorityView;
            const ss = statusStyle(e.status);
            return (
              <div key={it.id} style={{ display: "grid", gridTemplateColumns: "28px 1fr 1fr 1fr 110px", gap: 8, alignItems: "center", padding: "8px 12px", borderTop: "1px solid " + C.border, background: isP2 ? "#fffdf5" : C.white, borderLeft: isP2 ? "3px solid " + C.gold : "3px solid transparent" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>{it.num}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>{it.name}</div>
                  {it.phase && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: isP2 ? "#fef9c3" : "#f3f4f6", color: isP2 ? "#854d0e" : C.muted }}>{it.phase}</span>}
                  {it.p2Priority && <span style={{ fontSize: 10, marginLeft: 4, color: C.gold, fontWeight: 700 }}>P2 #{it.p2Priority}</span>}
                </div>
                <input value={e.biz || ""} onChange={function (ev) { setField(it.id, "biz", ev.target.value); }} placeholder={it.bizSug || ""}
                  style={{ width: "100%", padding: "4px 6px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                <input value={e.bi || ""} onChange={function (ev) { setField(it.id, "bi", ev.target.value); }} placeholder={it.biSug || ""}
                  style={{ width: "100%", padding: "4px 6px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                <button onClick={function () { cycleStatus(it.id); }}
                  style={{ padding: "3px 8px", borderRadius: 12, border: "1px solid " + C.border, cursor: "pointer", fontSize: 11, fontWeight: 700, background: ss.bg, color: ss.color }}>
                  {e.status || statusOptions[0]}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {view === "data" && (
        <div>
          {items.map(function (it) {
            const e = entryFor(it.id);
            const isP2 = !!it.inPriorityView;
            const isOpen = expanded === it.id;
            return (
              <Card key={it.id} style={{ border: isP2 ? "1px solid " + C.gold : "1px solid " + C.border }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: isOpen ? 12 : 0 }} onClick={function () { setExpanded(isOpen ? null : it.id); }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: C.muted, width: 20 }}>{it.num}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>{it.name}</span>
                    {it.phase && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: isP2 ? "#fef9c3" : "#f3f4f6", color: isP2 ? "#854d0e" : C.muted }}>{it.phase}</span>}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {e.dataGaps && <span style={{ fontSize: 10, color: C.green }}>gaps mapped</span>}
                    {e.definitionOfDone && <span style={{ fontSize: 10, color: C.blue }}>done defined</span>}
                    <span style={{ color: C.muted }}>{isOpen ? "\u25b2" : "\u25bc"}</span>
                  </div>
                </div>
                {isOpen && (
                  <div>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Definition of Done</div>
                      <textarea value={e.definitionOfDone != null ? e.definitionOfDone : (it.dod || "")} onChange={function (ev) { setField(it.id, "definitionOfDone", ev.target.value); }}
                        rows={2} style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
                    </div>
                    {it.etlNote && (
                      <div style={{ background: "#f0f7ff", borderRadius: 4, padding: "6px 10px", marginBottom: 8, fontSize: 11 }}>
                        <span style={{ fontWeight: 700, color: C.blue }}>ETL note: </span><span style={{ color: C.text }}>{it.etlNote}</span>
                      </div>
                    )}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Metrics driving this story</div>
                      <input value={e.metrics || ""} onChange={function (ev) { setField(it.id, "metrics", ev.target.value); }} placeholder="Revenue, Spread, Starts, Fill Rate, GP..."
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                    </div>
                    {cfg.sourceSystems && cfg.sourceSystems.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Source Systems Required</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {cfg.sourceSystems.map(function (sys) {
                            const selected = (e.sourceSystems || "").includes(sys);
                            return (
                              <button key={sys} onClick={function () {
                                const cur = (e.sourceSystems || "").split(", ").filter(Boolean);
                                const nxt = selected ? cur.filter(function (x) { return x !== sys; }) : [...cur, sys];
                                setField(it.id, "sourceSystems", nxt.join(", "));
                              }} style={{ padding: "3px 10px", borderRadius: 12, border: "1px solid " + (selected ? C.blue : C.border), background: selected ? C.blue + "22" : C.white, color: selected ? C.blue : C.muted, fontSize: 11, cursor: "pointer", fontWeight: selected ? 700 : 400 }}>
                                {sys}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Data Gaps</div>
                        <textarea value={e.dataGaps != null ? e.dataGaps : (it.keyGap || "")} onChange={function (ev) { setField(it.id, "dataGaps", ev.target.value); }}
                          rows={2} style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Gap Owner</div>
                        <input value={e.dataGapOwner || ""} onChange={function (ev) { setField(it.id, "dataGapOwner", ev.target.value); }} placeholder="Who fixes the gap..."
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Gap Must Close By</div>
                        <input type="date" value={e.dataGapDue || ""} onChange={function (ev) { setField(it.id, "dataGapDue", ev.target.value); }}
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Build Estimate</div>
                        <input value={e.buildEstimate || ""} onChange={function (ev) { setField(it.id, "buildEstimate", ev.target.value); }} placeholder="e.g. 2-3 weeks"
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Dependencies</div>
                        <input value={e.dependencies || ""} onChange={function (ev) { setField(it.id, "dependencies", ev.target.value); }} placeholder="Depends on..."
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {view === "priority" && (
        <div>
          {cfg.priorityBannerText && (
            <div style={{ background: "#fffdf5", border: "1px solid " + C.gold, borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 12, color: "#78350f" }}>{cfg.priorityBannerText}</div>
          )}
          {priorityItems.slice().sort(function (a, b) {
            const ea = entryFor(a.id); const eb = entryFor(b.id);
            const pa = ea.p2Priority != null ? ea.p2Priority : (a.p2Priority || 99);
            const pb = eb.p2Priority != null ? eb.p2Priority : (b.p2Priority || 99);
            return pa - pb;
          }).map(function (it, i, arr) {
            const e = entryFor(it.id);
            function move(dir) {
              const swapIdx = i + dir;
              if (swapIdx < 0 || swapIdx >= arr.length) return;
              const other = arr[swapIdx];
              const eOther = entryFor(other.id);
              const pThis = e.p2Priority != null ? e.p2Priority : it.p2Priority;
              const pOther = eOther.p2Priority != null ? eOther.p2Priority : other.p2Priority;
              setField(it.id, "p2Priority", pOther);
              setField(other.id, "p2Priority", pThis);
            }
            return (
              <Card key={it.id} style={{ border: "1px solid " + C.gold }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: C.gold, width: 32 }}>#{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.dark }}>{it.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>Biz: {e.biz || it.bizSug} | BI: {e.bi || it.biSug}</div>
                    {it.dod && <div style={{ fontSize: 11, color: C.blue, fontStyle: "italic", marginTop: 2 }}>{it.dod}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={function () { move(-1); }} disabled={i === 0}
                      style={{ padding: "4px 10px", border: "1px solid " + C.border, borderRadius: 4, cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.3 : 1, fontSize: 12 }}>▲</button>
                    <button onClick={function () { move(1); }} disabled={i === arr.length - 1}
                      style={{ padding: "4px 10px", border: "1px solid " + C.border, borderRadius: 4, cursor: i === arr.length - 1 ? "default" : "pointer", opacity: i === arr.length - 1 ? 0.3 : 1, fontSize: 12 }}>▼</button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FieldGroupBlock({ block, data, defaultData, updateItem, theme }) {
  const cfg = block.config;
  const layout = cfg.layout || "2col";
  const gridTemplateColumns = layout === "autofit" ? "repeat(auto-fit, minmax(180px, 1fr))" : layout === "1col" ? "1fr" : "1fr 1fr";
  const labelColor = theme === "dark-gold" ? "#9ca3af" : C.muted;
  return (
    <div>
      {cfg.intro && <div style={{ fontSize: 12, color: labelColor, marginBottom: 8 }}>{cfg.intro}</div>}
      <div style={{ display: "grid", gridTemplateColumns: gridTemplateColumns, gap: 10 }}>
        {(cfg.fields || []).map(function (field) {
          const value = get(data, defaultData, field.id, field.inputType === "multiselect" ? [] : "");
          return (
            <div key={field.id} style={field.inputType === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
              <div style={{ fontSize: 11, color: labelColor, marginBottom: 4 }}>{field.label}</div>
              <FieldInput field={field} value={value} onChange={function (v) { updateItem(field.id, v); }} theme={theme} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PilotTimeAndPlacementOutput({ num, cfg }) {
  const users = num("cbaPilotUsers");
  const timeMins = num("cbaTimeSavedMins");
  const hourlyRate = num("cbaHourlyCost");
  const avgSpread = num("cbaAvgSpread");
  const extraPlacements = num("cbaExtraPlacementsPerUser");
  const buildHours = num("cbaBuildHours");
  const engRate = num("cbaEngineerRate");

  const buildCost = buildHours * engRate;
  const infraCost = users * 500 * 0.000005 * 365;
  const cmCost = 50 * hourlyRate;
  const totalCost = buildCost + infraCost + cmCost;

  const timeBenefit = users * (timeMins / 60) * hourlyRate * 250;
  const placementBenefit = users * extraPlacements * avgSpread;
  const totalBenefit = timeBenefit + placementBenefit;

  const roi = totalCost > 0 ? ((totalBenefit - totalCost) / totalCost * 100) : 0;
  const paybackDays = totalBenefit > 0 ? Math.round((totalCost / totalBenefit) * 365) : 0;
  const ratioDisplay = totalCost > 0 ? (totalBenefit / totalCost).toFixed(1) : "0";

  const fmt = function (n) { return n >= 1000 ? "$" + (n / 1000).toFixed(0) + "K" : "$" + n.toFixed(0); };

  if (users === 0) {
    return (
      <div style={{ padding: "16px", background: "#f9fafb", borderRadius: 6, fontSize: 12, color: C.muted, textAlign: "center" }}>
        {cfg.emptyStateText || "Enter assumptions above to calculate the CBA live."}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div style={{ background: "#fef2f2", borderRadius: 6, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Year 1 Costs</div>
          {[
            ["Build cost", fmt(buildCost)],
            ["Infrastructure (LLM API)", fmt(infraCost)],
            ["Change management (~50hrs)", fmt(cmCost)],
          ].map(function (row) {
            return (
              <div key={row[0]} style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 12 }}>
                <span style={{ color: C.muted }}>{row[0]}</span>
                <span style={{ fontWeight: 700, color: C.red }}>{row[1]}</span>
              </div>
            );
          })}
          <div style={{ borderTop: "1px solid #fecaca", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}>
            <span style={{ color: C.red }}>Total Cost</span>
            <span style={{ color: C.red }}>{fmt(totalCost)}</span>
          </div>
        </div>
        <div style={{ background: "#d4edda", borderRadius: 6, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.green, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Year 1 Benefits</div>
          {[
            ["Time saved (" + timeMins + " min/day x " + users + " users)", fmt(timeBenefit)],
            ["Placement uplift (" + extraPlacements + " extra/user x $" + avgSpread.toLocaleString() + ")", fmt(placementBenefit)],
          ].map(function (row) {
            return (
              <div key={row[0]} style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 12 }}>
                <span style={{ color: "#155724" }}>{row[0]}</span>
                <span style={{ fontWeight: 700, color: C.green }}>{row[1]}</span>
              </div>
            );
          })}
          <div style={{ borderTop: "1px solid #b7dfbb", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}>
            <span style={{ color: C.green }}>Total Benefit</span>
            <span style={{ color: C.green }}>{fmt(totalBenefit)}</span>
          </div>
        </div>
      </div>
      <div style={{ background: C.dark, borderRadius: 6, padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
        {[
          ["Net Year 1 Benefit", fmt(totalBenefit - totalCost), totalBenefit > totalCost ? C.green : C.red],
          ["ROI", roi.toFixed(0) + "%", roi > 0 ? C.green : C.red],
          ["Payback", paybackDays + " days", paybackDays < 90 ? C.green : C.orange],
          ["Benefit:Cost Ratio", ratioDisplay + "x", C.gold],
        ].map(function (arr) {
          return (
            <div key={arr[0]} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 3 }}>{arr[0]}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: arr[2] }}>{arr[1]}</div>
            </div>
          );
        })}
      </div>
      {cfg.assumptionsNote && (
        <div style={{ marginTop: 8, fontSize: 10, color: C.muted, fontStyle: "italic" }}>{cfg.assumptionsNote}</div>
      )}
    </div>
  );
}

function CalculatorBlock({ block, data, defaultData, updateItem }) {
  const cfg = block.config;
  function val(key) { return get(data, defaultData, key, ""); }
  function num(key) { return parseFloat(val(key)) || 0; }

  return (
    <div>
      {cfg.intro && <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>{cfg.intro}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 14 }}>
        {(cfg.inputs || []).map(function (inp) {
          return (
            <div key={inp.key}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{inp.label}</div>
              <div style={{ display: "flex", alignItems: "center", border: "1px solid " + C.border, borderRadius: 6, overflow: "hidden", background: C.white }}>
                <input type="number" value={val(inp.key) || ""} onChange={function (e) { updateItem(inp.key, e.target.value); }}
                  placeholder={inp.placeholder || ""}
                  style={{ flex: 1, padding: "7px 10px", fontSize: 13, fontWeight: 700, border: "none", outline: "none", background: "transparent", color: C.dark, minWidth: 0 }} />
                {inp.suffix && <span style={{ fontSize: 11, color: C.white, background: C.muted, padding: "7px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>{inp.suffix}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {cfg.formulaSet === "pilot_time_and_placement_v1" && <PilotTimeAndPlacementOutput num={num} cfg={cfg} />}
    </div>
  );
}

function Footer({ footerConfig, data, defaultData, updateItem }) {
  if (!footerConfig) return null;
  const ackKey = footerConfig.acknowledgmentKey;
  const notesKey = footerConfig.notesKey;
  const acked = ackKey ? !!get(data, defaultData, ackKey, false) : false;
  const notes = notesKey ? (get(data, defaultData, notesKey, "") || "") : "";
  return (
    <div style={{ marginTop: 4 }}>
      {(footerConfig.fields || []).map(function (f) {
        const v = get(data, defaultData, f.key, "") || "";
        return (
          <div key={f.key} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{f.label}</div>
            <textarea value={v} onChange={function (e) { updateItem(f.key, e.target.value); }} placeholder={f.placeholder || ""} rows={2}
              style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
          </div>
        );
      })}
      {ackKey && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <input type="checkbox" checked={acked} onChange={function (e) { updateItem(ackKey, e.target.checked); }} style={{ cursor: "pointer" }} />
          <label style={{ fontSize: 12, color: acked ? C.green : C.dark, fontWeight: acked ? 700 : 400, cursor: "pointer" }}
            onClick={function () { updateItem(ackKey, !acked); }}>{footerConfig.acknowledgmentLabel || "Acknowledged"}</label>
        </div>
      )}
      {notesKey && (
        <div>
          {footerConfig.notesLabel && <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{footerConfig.notesLabel}</div>}
          <textarea value={notes} onChange={function (e) { updateItem(notesKey, e.target.value); }} placeholder={footerConfig.notesPlaceholder || ""} rows={3}
            style={{ width: "100%", padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
        </div>
      )}
    </div>
  );
}

function renderBlock(block, data, defaultData, updateItem, theme) {
  if (block.type === "scorecard") return <ScorecardBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "gate_checklist") return <GateChecklistBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "matrix") return <MatrixBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "list") return <ListBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "narrative") return <NarrativeBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "tracker") return <TrackerBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "decision_log") return <AreaFieldsBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} fieldVocab={DECISION_FIELD_VOCAB} />;
  if (block.type === "commitment_log") return <AreaFieldsBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} fieldVocab={COMMITMENT_FIELD_VOCAB} />;
  if (block.type === "current_state") return <AreaFieldsBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} fieldVocab={CURRENT_STATE_FIELD_VOCAB} />;
  if (block.type === "settings_card") return <SettingsCardBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "story_tracker") return <StoryTrackerBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "ranked_scorecard") return <RankedScorecardBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  if (block.type === "field_group") return <FieldGroupBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} theme={theme} />;
  if (block.type === "calculator") return <CalculatorBlock block={block} data={data} defaultData={defaultData} updateItem={updateItem} />;
  return <div style={{ color: C.red, fontSize: 12 }}>Unknown block type: {block.type}</div>;
}

// Returns { items, getItemData } for a block, normalized across every
// block type's own storage convention -- used by tabConfig.stats[] and
// tabConfig.csvExport to source from any block without duplicating each
// block type's own item/entry access logic.
function getBlockItemsAndAccessor(block, data, defaultData) {
  if (!block) return { items: [], getItemData: function () { return {}; } };
  if (block.type === "narrative") {
    const entriesKey = block.config.storageKey || ("_entries_" + block.id);
    const entries = get(data, defaultData, entriesKey, []);
    return { items: entries, getItemData: function (it) { return it; } };
  }
  const items = block.config.areas || block.config.items || [];
  return { items: items, getItemData: function (it) { return get(data, defaultData, it.id, {}); } };
}

function csvValue(v) {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return v || "";
}

// Builds one CSV section's header + rows for a block, reusing the same
// fields it's already configured with (fieldDef.label for list/tracker/
// narrative, or captureFields/fieldVocab for decision_log/commitment_log/
// current_state) -- the same dynamic-header-from-configured-fields
// convention scorecard/ranked_scorecard's own csvFilename already uses.
function buildCsvSection(block, data, defaultData) {
  const { items, getItemData } = getBlockItemsAndAccessor(block, data, defaultData);
  let fieldDefs;
  if (block.config.fields) {
    fieldDefs = block.config.fields;
  } else {
    const vocab = block.type === "decision_log" ? DECISION_FIELD_VOCAB : block.type === "commitment_log" ? COMMITMENT_FIELD_VOCAB : CURRENT_STATE_FIELD_VOCAB;
    const captureFields = (block.config.captureFields && block.config.captureFields.length) ? block.config.captureFields : Object.keys(vocab);
    fieldDefs = captureFields.map(function (f) { return { id: f, label: (vocab[f] && vocab[f].label) || f }; });
  }
  const header = [block.config.itemLabel || block.title].concat(fieldDefs.map(function (f) { return f.label; }));
  const rows = items.map(function (it) {
    const d = getItemData(it);
    const label = it.label || it.text || "";
    return [label].concat(fieldDefs.map(function (f) { return csvValue(d[f.id]); }));
  });
  return { header: header, rows: rows };
}

function exportTabCsv(tabConfig, data, defaultData) {
  const rows = [];
  (tabConfig.csvExport.sections || []).forEach(function (section, i) {
    const block = (tabConfig.blocks || []).find(function (b) { return b.id === section.sourceBlockId; });
    if (!block) return;
    const built = buildCsvSection(block, data, defaultData);
    if (i > 0) rows.push([""]);
    rows.push(section.header || built.header);
    built.rows.forEach(function (r) { rows.push(r); });
  });
  csvDownload(tabConfig.csvExport.filename, rows);
}

// tabConfig: an object matching capture-tab-schema.json (id, name, category,
// description, storageKey, footer, blocks). projectConfig: passed straight
// through to useSummitSync, same as the hardcoded capture components.
export default function GenericCaptureRenderer({ tabConfig, projectConfig, previewMode }) {
  const defaultData = buildDefaultData(tabConfig);
  const { data, update, saveStatus, loaded } = useSummitSync(previewMode ? null : tabConfig.storageKey, defaultData, projectConfig);

  function updateItem(key, updater) {
    update(function (prev) {
      const prevVal = (prev && prev[key] !== undefined) ? prev[key] : defaultData[key];
      const nextVal = typeof updater === "function" ? updater(prevVal) : updater;
      return { ...prev, [key]: nextVal };
    });
  }

  if (!loaded) {
    return <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Loading...</div>;
  }

  return (
    <div>
      <SectionHeader title={tabConfig.name} subtitle={tabConfig.description} />
      {tabConfig.facilitatorGuide && <FacilitatorNote text={tabConfig.facilitatorGuide} />}
      <TabStatsRow stats={tabConfig.stats} tabConfig={tabConfig} data={data} defaultData={defaultData} />
      {(tabConfig.blocks || []).map(function (block) {
        const dark = block.blockStyle === "dark-gold";
        return (
          <Card key={block.id} style={dark ? { background: C.dark, border: "none" } : undefined}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: dark ? C.gold : C.dark }}>{block.title}</div>
              <SaveStatusIndicator status={saveStatus} />
            </div>
            {block.facilitatorNote && <FacilitatorNote text={block.facilitatorNote} />}
            {renderBlock(block, data, defaultData, updateItem, dark ? "dark-gold" : undefined)}
          </Card>
        );
      })}
      {tabConfig.csvExport && (
        <button type="button" onClick={function () { exportTabCsv(tabConfig, data, defaultData); }}
          style={{ padding: "8px 16px", background: C.white, color: C.dark, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontSize: 12, marginBottom: 12 }}>
          Export CSV
        </button>
      )}
      {tabConfig.footer && (
        <Card>
          <Footer footerConfig={tabConfig.footer} data={data} defaultData={defaultData} updateItem={updateItem} />
        </Card>
      )}
    </div>
  );
}
